import { Component, OnInit } from "@angular/core";
import { Router, NavigationStart, RouterOutlet } from "@angular/router";
import { AlertsService } from "./service/alerts.service";
import { ChatService } from "./service/chat.service";
import { UserService } from "./service/user.service";
import { LoaderService } from "./service/loader.service";
import { FooterComponent } from "./component/footer/footer.component";
import { AlertsComponent } from "./component/alerts/alerts.component";
import { NewSingleChatComponent } from "./component/new-single-chat/new-single-chat.component";
import { NewGroupChatComponent } from "./component/new-group-chat/new-group-chat.component";
import { OnlineUsersService } from "./service/online-users.service";
import { trigger, transition, style, animate } from "@angular/animations";
import { AuthService } from "./service/auth-service";
import { PwaService } from "./service/pwa.service";
import { MatSnackBar } from '@angular/material/snack-bar';
import { LinkWorkspaceService } from "./component/link-workspace/link-workspace.service";
import { NotificationSoundService, soundKindOf } from "./service/notification-sound.service";
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { selectIsLoading } from './store/loading/loading.selectors';
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { AsyncPipe } from '@angular/common';
import { ThemeService } from "./service/theme.service";
import { AppUpdateService } from './service/app-update.service';
import { UpdateButtonComponent } from './component/update-button/update-button.component';
import { messaging } from '../../firebase-config';
import { getFcmServiceWorker, getOrRefreshFcmToken } from './service/fcm-service-worker';
import { TaskComposerComponent } from './features/tasks/components/task-composer/task-composer.component';

declare var $: any;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  imports: [
    NewGroupChatComponent,
    NewSingleChatComponent,
    RouterOutlet,
    AlertsComponent,
    FooterComponent,
    MatProgressBarModule,
    AsyncPipe,
    TaskComposerComponent,
    UpdateButtonComponent,
  ],
  animations: [
    trigger("slideIn", [
      transition(":enter", [
        style({ transform: "translateX(100%)", opacity: 0 }),
        animate("300ms ease-in", style({ transform: "translateX(0)", opacity: 1 })),
      ]),
      transition(":leave", [
        animate("300ms ease-out", style({ transform: "translateX(100%)", opacity: 0 })),
      ]),
    ]),
  ]
})
export class AppComponent implements OnInit {
  title = "link-frontend";
  showChat = false;
  message;
  audio = new Audio("../assets/bell-v2.mp3");
  User = "";
  isMobile: boolean;
  isLoading$: Observable<boolean>;
  /** Set while an Admin is looking at someone's account (token claim auditBy): a read-only hour. */
  audit: { userName: string; until: string } | null = null;

  constructor(
    public router: Router,
    public chatService: ChatService,
    private userService: UserService,
    public loader: LoaderService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private workspaceService: LinkWorkspaceService,
    private soundService: NotificationSoundService,
    private store: Store,
    private themeService: ThemeService,
    public appUpdate: AppUpdateService,
    private pwaService: PwaService
  ) {
    // New versions (30 Oct 2026, founder): an Update button instead of reloading in the middle of work.
    this.appUpdate.start();
    this.isLoading$ = this.store.select(selectIsLoading);

    router.events.forEach((event) => {
      if (event instanceof NavigationStart) {
        if (
          event["url"] == "/login" ||
          event["url"] === "/" ||
          event["url"].includes("/video-call/") ||
          event["url"] == "/registration"
        ) {
          this.showChat = false;
        } else {
          this.showChat = true;
        }
      }
    });
  }
  showInstallTooltip = false;

  refreshAudit(): void {
    const p = this.authService.getTokenPayload();
    this.audit = p?.auditBy
      ? { userName: p.userName || '', until: p.auditUntil ? new Date(p.auditUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '' }
      : null;
  }
  leaveAudit(): void {
    this.authService.forceLogout();
  }

  private openNotificationUrl(url: string): void {
    let target: URL;
    try {
      target = new URL(url, window.location.href);
    } catch {
      return;
    }
    if (target.origin !== window.location.origin || !target.hash) {
      window.location.href = target.href;
      return;
    }
    const tree = this.router.parseUrl(target.hash.slice(1) || '/');
    tree.queryParams = { ...tree.queryParams, n: Date.now().toString() };
    try {
      sessionStorage.setItem('fcm_notification_navigation', Date.now().toString());
    } catch (e) {}
    void this.router.navigateByUrl(tree, { state: { fromNotification: true } });
  }

  /**
   * Asks the FCM worker for a notification link this tab has not routed to yet, clearing it.
   * The worker keeps one for a minute after a click in case the tab reloaded on focus and
   * missed OPEN_NOTIFICATION_URL; a tab that did get the message calls this only to clear it.
   */
  private async consumePendingNotificationUrl(): Promise<string | null> {
    const worker = await getFcmServiceWorker();
    if (!worker) return null;
    return new Promise((resolve) => {
      const channel = new MessageChannel();
      const timer = setTimeout(() => resolve(null), 3000);
      channel.port1.onmessage = (e) => {
        clearTimeout(timer);
        resolve(e.data?.url || null);
      };
      worker.postMessage({ type: 'CONSUME_NOTIFICATION_URL' }, [channel.port2]);
    });
  }

  ngOnInit() {
    this.refreshAudit();
    this.router.events.subscribe(() => this.refreshAudit());
    // New versions are checked for and offered by AppUpdateService (the Update button), started in the constructor.
    setInterval(() => {
      this.authService.scheduleTokenRefresh();
    }, 60 * 60 * 1000); // 1 hour in milliseconds

    this.syncFcmTokenOnBoot()
    this.chatService.listenNotificationMsg();
    // index.html keeps the browser's install prompt; offer the Install tooltip once when Link is installable.
    this.pwaService.isInstallable.subscribe((installable) => {
      if (installable && !localStorage.getItem("pwa-tooltip-shown")) {
        this.showInstallTooltip = true;
        localStorage.setItem("pwa-tooltip-shown", "true");
      }
    });


    // Listen for Service Worker background notification sound trigger (when window is minimized)
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if (event.data && event.data.type === 'PLAY_NOTIFICATION_SOUND') {
          console.log("SW background trigger received:", event.data);
          this.soundService.play(this.soundService.normalizeKind(event.data.soundKind, event.data.payload));
        }
        if (event.data && event.data.type === 'OPEN_NOTIFICATION_URL') {
          this.openNotificationUrl(event.data.url);
          void this.consumePendingNotificationUrl();
        }
      });

      // A notification click that focused this tab while it was reloading.
      void this.consumePendingNotificationUrl().then((url) => {
        if (url) this.openNotificationUrl(url);
      });

      navigator.serviceWorker.ready.then(() => {
        this.soundService.syncAllSettings();
      });
    }

    //  foreground notification
    this.chatService.currentMessage$.subscribe((payload) => {
      console.log("Notification received in component:", payload);

      let parsedData: any = null;

      try {
        parsedData = payload?.data?.payload
          ? JSON.parse(payload.data.payload)
          : null;
      } catch (e) {
        console.warn("Invalid payload JSON");
        return;
      }
      if (!parsedData) return;

      // Each family has its own chime and can be switched off on this device (settings → Notifications).
      const kind = soundKindOf(parsedData);
      if (!this.soundService.isEnabled(kind) || this.soundService.isPaused()) return;
      this.soundService.play(kind);

      const redirectUrl =
        parsedData.meta?.redirectUrl || parsedData.redirectUrl || "";

      if (!redirectUrl) return;
      console.log("Showing foreground notification for:", redirectUrl);
      this.showForegroundNotification(parsedData.title || 'Notification', parsedData.body || parsedData.message || 'New update available', redirectUrl);
    });
  }

  // Login is otherwise the only place a token reaches the backend. A token that
  // rotates (FCM ngOndoes this on its own schedule) or a push subscription that
  // changes underneath it (e.g. this app's service worker moving scope) had no
  // way back into the database until the user's next login — silently, since
  // nothing here throws when it happens. Runs on every boot for an already
  // logged-in user and only calls the backend when the token actually changed.

  private async syncFcmTokenOnBoot(): Promise<void> {
    const guestID = localStorage.getItem('guestID');
    const browserUniqueId = localStorage.getItem('browserUniqueId');

    console.log(
      '[FCM Diagnostics] syncFcmTokenOnBoot running.',
      { guestID, browserUniqueId }
    );

    if (!guestID || !browserUniqueId) return;

    const token = await getOrRefreshFcmToken(messaging);

    if (!token) {
      console.warn('[FCM Diagnostics] No FCM token obtained on boot');
      return;
    }

    // Always sync the token on app load, even if unchanged.
    this.userService.updateToken({
      id: guestID,
      token,
      browserUniqueId
    }).subscribe({
      next: () =>
        console.log('[FCM Diagnostics] FCM token synced successfully'),
      error: (err) =>
        console.error('[FCM Diagnostics] Failed to sync FCM token:', err)
    });
  }


  showForegroundNotification(title: string, message: string, redirectUrl: string) {
    this.snackBar
      .open(`${title}: ${message}`, 'Open', {
        duration: 6000,
        verticalPosition: 'top',
        horizontalPosition: 'right'
      })
      .onAction()
      .subscribe(() => {
        // In-app paths (Tasks and other hash routes) stay inside the app; full URLs open as before.
        try {
          sessionStorage.setItem('fcm_notification_navigation', Date.now().toString());
        } catch (e) {}
        if (redirectUrl.startsWith('/')) this.router.navigateByUrl(redirectUrl, { state: { fromNotification: true } });
        else window.open(redirectUrl, '_self');
      });
  }

  installPWA() {
    this.showInstallTooltip = false;
    void this.pwaService.installPWA();
  }

  closeTooltip() {
    this.showInstallTooltip = false;
  }

  onReloadDoc() {
    console.log("in fnc");
    window.postMessage("update", "*");
  }

  nameCaller(string) {
    return string[0].toUpperCase() + string.slice(1);
  }

  onClickDecline() {
    document.getElementById("alert").classList.add("hide");
    this.audio.pause();
    this.audio.currentTime = 0;
  }

  onClickAccept() {
    document.getElementById("alert").classList.add("hide");
    this.audio.pause();
    this.audio.currentTime = 0;
  }

  shouldShowChat(): boolean {
    const currentRoute = this.router.url;
    return currentRoute.includes("/home");
  }

  onClickLogout() {
    this.chatService.disconnectSocket();

    const browserUniqueId = localStorage.getItem("browserUniqueId");
    const guestID = localStorage.getItem("guestID");
    const fcmToken = localStorage.getItem("fcm_token");

    if (browserUniqueId) {
      this.userService.logOut(browserUniqueId).subscribe({
        next: () => {
          console.log("Browser logout success");
        },
        error: (err) => {
          console.error("Logout API failed:", err);
        },
      });
    }

    this.soundService.syncToServiceWorker('active_user_id', null);
    this.workspaceService.clearState();
    const savedThemes = ThemeService.preserveTheme();
    localStorage.clear();
    ThemeService.restoreTheme(savedThemes);
    if (browserUniqueId) {
      localStorage.setItem("browserUniqueId", browserUniqueId);
    }

    this.router.navigate(["/login"]);
  }
}
